import { Component, OnInit, OnDestroy } from "@angular/core";
import {
    ITCIAppEnvControlProviderRequest,
    ITCIApplicationDeployRequest,
    TCI_ENV_CTRL_ACTION_TYPE,
    TCITenantSelectorType,
    TCIApplicationDeploymentMode,
    USER_ROLES,
    ITCIEnvVars,
    TCI_ENVIRONMENT_VAR_TYPE,
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../../../ng-tenant/tci-tenant-component";
import { BaseSubmenuHybridConnService } from "./base-submenu-hybrid-conn.service";
import { SelectItem } from "primeng/primeng";
import { Observable, Subscription } from "rxjs";
import { NgToastrService } from "../../../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENV_HYBRID_CONN.toString(),
    templateUrl: "./base-submenu-hybrid-conn.component.html",
    styleUrls: ["./base-submenu-hybrid-conn.component.less"]
})
export class BaseSubmenuHybridConnComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {

    accessKeyList: SelectItem[] = [];
    selectedAccessKey = null;
    disableField = false;
    isRORoleUser = false;
    isAccessKeyValid = true;
    isCIC2 = false;

    // Subscriptions collection
    private subscriptions: Subscription[] = [];
    private isShowDropdownList = false;

    constructor(private hybridConnService: BaseSubmenuHybridConnService, private toastr: NgToastrService) {
        super();
    }

    ngOnInit(): void {
        this.initHybridConnectivity();

        // Request current state
        const stateRequest: ITCIApplicationDeployRequest = {
            mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
            appId: this.input.application.id,
            application: this.input.application
        };

        this.subscriptions.push(
            this.hybridConnService
                .getEnvCtrlService()
                .getTenantDeployProvider(this.input.tenantAppType)
                .isBusy(stateRequest)
                .subscribe(busy => {
                    this.disableField = busy;
                })
        );

        const envObs$ = this.hybridConnService
            .getEnvVarsService()
            .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.IS_CIC2_ENV)
            .map((envVars: ITCIEnvVars<string>) => !!envVars[TCI_ENVIRONMENT_VAR_TYPE.IS_CIC2_ENV]);

        const userObs$ = this.hybridConnService
            .getEnvCtrlService()
            .getNgUserService()
            .getUserInfo()
            .map(res => !!res.clusterId);

        this.subscriptions.push(
            Observable.combineLatest(envObs$, userObs$, (isCIC2, isClusterId) => ({ isCIC2, isClusterId }))
            .subscribe(res => {
                this.isCIC2 = res.isCIC2 && res.isClusterId;
            })
        );

        this.subscriptions.push(this.hybridConnService.getEnvCtrlService().getStatusSubject()
            .subscribe((res: TCI_ENV_CTRL_ACTION_TYPE) => {
                if (res === TCI_ENV_CTRL_ACTION_TYPE.REFRESH) {
                    this.initHybridConnectivity();
                }
            }));

        this.hybridConnService.getEnvCtrlService().getUserAuthForEnv()
            .subscribe(userAuth => {
                this.isRORoleUser = (userAuth.role === USER_ROLES.RO);
            });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    initHybridConnectivity() {
        this.selectedAccessKey = this.input.application.tibTunnelAccessKey;
        this.accessKeyList = [{
            label: "No Access Key",
            value: null
        }];
        this.hybridConnService.getEnvCtrlService().initHybridConnectivity(this.generateReqInput())
            .subscribe(
                res => {
                    this.isAccessKeyValid = res.accessKeyList.indexOf(this.selectedAccessKey) !== -1;
                    res.accessKeyList.forEach(key => {
                        this.accessKeyList.push({
                            label: key,
                            value: key
                        });
                    });
                });
    }

    generateReqInput(): ITCIAppEnvControlProviderRequest {
        return {
            actionType: TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY,
            appId: this.input.application.id,
            application: this.input.application,
            tenantType: this.input.application.type
        };
    }

    toggleDropdown() {
        this.isShowDropdownList = !this.isShowDropdownList;
    }

    updateChange(accessKeyValue) {
        this.toggleDropdown();
        this.selectedAccessKey = accessKeyValue;
        // hide the warning message
        this.isAccessKeyValid = true;
        this.hybridConnService.getEnvCtrlService().setTabData(TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY, accessKeyValue);
    }

    get connectionUrl(): string {
        return this.input.application.id ? `tunnel/${this.input.application.id}` : "-";
    }

    notifyCopied(connUrl: string) {
        this.toastr.success(`${connUrl} copied to clipboard.`);
    }
}
